import { ThemeConfig } from 'antd'
import baseTheme from './theme'
import baseAntdTheme from './antd-theme'

export const SYSTEM_THEME_COMPONENT_KEYS = [
  'button',
  'checkbox',
  'steps',
  'activeMenu',
  'focus',
] as const

export type SystemThemeComponentKey = (typeof SYSTEM_THEME_COMPONENT_KEYS)[number]

export interface SystemThemePayload {
  primary: string
  components: Record<SystemThemeComponentKey, string | null>
}

export interface ResolvedSystemTheme {
  primary: string
  button: string
  checkbox: string
  steps: string
  activeMenu: string
  focus: string
}

export const DEFAULT_SYSTEM_THEME: SystemThemePayload = {
  primary: '#1677ff',
  components: {
    button: null,
    checkbox: null,
    steps: null,
    activeMenu: null,
    focus: null,
  },
}

const HEX_COLOR_REGEXP = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

export const isHexColor = (value?: string | null): value is string =>
  typeof value === 'string' && HEX_COLOR_REGEXP.test(value.trim())

const normalizeHexColor = (value: string) => value.trim().toLowerCase()

export const normalizeSystemTheme = (
  value?: Partial<SystemThemePayload> | null,
): SystemThemePayload => {
  const rawPrimary = value?.primary
  const primary = isHexColor(rawPrimary)
    ? normalizeHexColor(rawPrimary)
    : DEFAULT_SYSTEM_THEME.primary
  const sourceComponents = value?.components || {}

  const components = SYSTEM_THEME_COMPONENT_KEYS.reduce(
    (acc, key) => {
      const componentValue = sourceComponents[key]
      acc[key] = isHexColor(componentValue) ? normalizeHexColor(componentValue) : null
      return acc
    },
    { ...DEFAULT_SYSTEM_THEME.components },
  )

  return { primary, components }
}

export const resolveSystemTheme = (
  value?: Partial<SystemThemePayload> | null,
): ResolvedSystemTheme => {
  const theme = normalizeSystemTheme(value)

  return {
    primary: theme.primary,
    button: theme.components.button || theme.primary,
    checkbox: theme.components.checkbox || theme.primary,
    steps: theme.components.steps || theme.primary,
    activeMenu: theme.components.activeMenu || theme.primary,
    focus: theme.components.focus || theme.primary,
  }
}

const expandHexColor = (color: string) => {
  const hex = color.replace('#', '')
  if (hex.length === 3) {
    return hex
      .split('')
      .map(char => `${char}${char}`)
      .join('')
  }
  return hex
}

export const hexToRgb = (color: string) => {
  const normalized = expandHexColor(color)
  const value = Number.parseInt(normalized, 16)

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  }
}

export const hexToRgba = (color: string, alpha: number) => {
  const { r, g, b } = hexToRgb(color)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const applySystemThemeCssVars = (theme: ResolvedSystemTheme) => {
  const root = document.documentElement
  root.style.setProperty('--system-primary', theme.primary)
  root.style.setProperty('--system-button', theme.button)
  root.style.setProperty('--system-checkbox', theme.checkbox)
  root.style.setProperty('--system-steps', theme.steps)
  root.style.setProperty('--system-active-menu', theme.activeMenu)
  root.style.setProperty('--system-focus', theme.focus)
  root.style.setProperty('--system-focus-shadow', hexToRgba(theme.focus, 0.2))
  root.style.setProperty('--system-primary-subtle', hexToRgba(theme.primary, 0.08))
  root.style.setProperty('--system-steps-subtle', hexToRgba(theme.steps, 0.12))
}

export const createStyledTheme = (systemTheme: ResolvedSystemTheme) => ({
  ...baseTheme,
  colors: {
    ...baseTheme.colors,
    primary: systemTheme.primary,
  },
})

export const createAntdTheme = (systemTheme: ResolvedSystemTheme): ThemeConfig => ({
  ...baseAntdTheme,
  token: {
    ...(baseAntdTheme.token || {}),
    colorPrimary: systemTheme.primary,
    colorLink: systemTheme.primary,
  },
  components: {
    ...(baseAntdTheme.components || {}),
    Button: {
      ...baseAntdTheme.components?.Button,
      colorPrimary: systemTheme.button,
      colorPrimaryHover: systemTheme.button,
      colorPrimaryActive: systemTheme.button,
    },
    Checkbox: {
      ...baseAntdTheme.components?.Checkbox,
      colorPrimary: systemTheme.checkbox,
      colorPrimaryHover: systemTheme.checkbox,
    },
    Radio: {
      ...baseAntdTheme.components?.Radio,
      colorPrimary: systemTheme.checkbox,
    },
    Switch: {
      ...baseAntdTheme.components?.Switch,
      colorPrimary: systemTheme.checkbox,
      colorPrimaryHover: systemTheme.checkbox,
    },
    Steps: {
      ...baseAntdTheme.components?.Steps,
      colorPrimary: systemTheme.steps,
    },
    Menu: {
      ...baseAntdTheme.components?.Menu,
      itemSelectedBg: systemTheme.activeMenu,
      itemSelectedColor: '#ffffff',
      darkItemSelectedBg: systemTheme.activeMenu,
      darkItemSelectedColor: '#ffffff',
    },
    Input: {
      ...baseAntdTheme.components?.Input,
      activeBorderColor: systemTheme.focus,
    },
    Select: {
      ...baseAntdTheme.components?.Select,
      activeBorderColor: systemTheme.focus,
      controlOutline: hexToRgba(systemTheme.focus, 0.2),
    },
  },
})
